<template>
	<modal
		:active="active"
		:dismissable="dismissable"
        :loading="loading"
		title="TV Mode"
        class="start-tv-modal"
		@dismiss="dismiss"
	>

		<modal-body>
		
			<p>TV Mode automatically rotates one or more dashboards for your big screen monitor.</p>

			<form @submit.prevent="create">

				<form-section label-width="120">

                    <control-group title="Rotation interval">
						<select v-control v-model="rotationInterval">
							<option v-for="interval in rotationIntervals" :key="interval" :value="interval">{{ interval | interval }}</option>
						</select>
					</control-group>

                    <control-group v-if="!ready" title="Dashboards">
                        <text-loader v-control:static>Loading dashboards</text-loader>
					</control-group>

                    <control-group v-else-if="dashboardGroups.length === 1" :valid="!$v.dashboardIDs.$error" title="Dashboards">
                        <template v-if="$v.dashboardIDs.$error">
							<control-error v-control:static v-if="!$v.dashboardIDs.required || !$v.dashboardIDs.minLength">Choose at least one dashboard</control-error>
						</template>

                        <template v-if="ready">
                            <checkbox
                                v-model="dashboardIDs"
                                v-focus
                                v-for="dashboard in dashboards"
                                :key="dashboard.dashboardID"
                                :label="dashboard.dashboardName"
                                :value="dashboard.dashboardID"
                                @change="$v.dashboardIDs.$touch()"
                            />
                        </template>
					</control-group>

                    <control-group v-else :valid="!$v.dashboardIDs.$error" title="Dashboards">
                        <template v-if="$v.dashboardIDs.$error">
							<control-error v-control:static v-if="!$v.dashboardIDs.required || !$v.dashboardIDs.minLength">Choose at least one dashboard</control-error>
						</template>

                        <div v-for="groupPair in dashboardGroups" :key="groupPair[0]" class="checkbox-list">
                            <h6 class="checkbox-list-header">{{groupPair[0]}}</h6>

                            <template v-if="ready">
					            <checkbox
                                    v-model="dashboardIDs"
                                    v-focus
                                    v-for="dashboard in groupPair[1]"
                                    :key="dashboard.dashboardID"
                                    :label="dashboard.dashboardName"
                                    :value="dashboard.dashboardID"
                                    @change="$v.dashboardIDs.$touch()"
                                />
                            </template>
                        </div>
					</control-group>

				</form-section>

			</form>

		</modal-body>

		<modal-actions>
			<btn primary @click="start">Start</btn>
			<btn link @click="cancel">Cancel</btn>
		</modal-actions>

	</modal>
</template>


<script>
    import _ from 'lodash';
    import Vue from 'vue';
    import BigScreen from 'bigscreen';
    import { minLength, required } from 'validators';
	import { AsyncMixin, FormValidationMixin, ModalMixin } from 'pdk/mixins';
    import { actions } from 'dashboard-pdk/store';
    import { actions as appStoreActions } from '@/store';
    import { dashboards } from 'dashboard-pdk/services';
    import { auth } from '@/services';


	export default {
        name: 'start-tv-modal',
        filters: {
		    interval(ms) {
			    if (ms < 60000) {
				    const seconds = ms / 1000;
				    const label = seconds === 1 ? 'second' : 'seconds';
				    return `${seconds} ${label}`;
			    } if (ms < 3600000) {
				    const minutes = ms / 60000;
				    const label = minutes === 1 ? 'minute' : 'minutes';
				    return `${minutes} ${label}`;
			    }
			    const hours = ms / 3600000;
			    const label = hours === 1 ? 'hour' : 'hours';
			    return `${hours} ${label}`;			
		    }
	    },
        mixins: [AsyncMixin, ModalMixin, FormValidationMixin],
        props: {
            dismissable: {
                type: Boolean,
                default: true
            }
        },
		data() {
            return {
                dashboardIDs: [],
                dashboards: [],
                rotationInterval: 60000,
                rotationIntervals: [30000, 60000, 300000, 600000, 900000, 1800000, 3600000, 10800000, 21600000, 43200000, 86400000],
                ready: false
			};
		},
        validations: {
            dashboardIDs: {
                required,
                minLength: minLength(1)
            }
		},
		computed: {
            userPreferences() {
                return this.$store.state.auth.userPreferences;
            },
            groups() {
                return this.$store.state.dashboard.locationGroups;
            },
            dashboardGroups() {
                const groups = _.groupBy(this.dashboards, (d) => {
                    const group = _.find(this.groups, (g) => g.locationGroupID == d.locationGroupID);
                    if (!group) return null;
                    return group.reportName;
                });

                const groupPairs = _.toPairs(groups);
                const orderedGroupPairs = _.orderBy(groupPairs, [(g) => g[0]]);

                return orderedGroupPairs;
            },
            selectedDashboards() {
                return _.filter(this.dashboards, (d) => _.includes(this.dashboardIDs, d.dashboardID));
            }
		},
        methods: {
            onOpen() {
                this.dashboards = [];

                this.async(dashboards.getDashboards())
                    .then((dashboardList) => {
                        this.dashboards = dashboardList;

                        this.async(this.updateUserPreferencesWhenAvailable())
                            .then(() => {
                                this.ready = true;
                            });
                    });
            },

            start() {
                if (!this.validate()) return;

                this.$store.dispatch(actions.DASHBOARDS_SET, {
                    dashboards: this.selectedDashboards
                });

                BigScreen.onenter = () => {
                    document.getElementsByTagName('body')[0].classList.add('fullscreen');
                };
                BigScreen.onexit = () => {
                    document.getElementsByTagName('body')[0].classList.remove('fullscreen');
                };
                BigScreen.onchange = (e) => {
                    // There are no elements in big screen mode, indicating it is closing.
                    if (e === null) {
                        this.$store.dispatch(actions.CLEAR);
                        this.$store.dispatch(appStoreActions.LOGOUT);
                    }
                };

                BigScreen.toggle();


                // Save the changes and start
                this.async(this.$store.dispatch(appStoreActions.USER_PREFERENCES_UPDATE, {
                    tvDashboardIDs: this.dashboardIDs,
                    tvDashboardRotationInterval: this.rotationInterval
                }))
                    .then(() => {
                            this.$store.dispatch(actions.TV_ACTIVATE, {
                                tvDashboardIDs: this.dashboardIDs
                            }).then(() => {
                                this.$emit('start');
                                this.close();

                                this.$router.push({ name: 'tv' });
                            });
                    });
            },

            updateUserPreferencesWhenAvailable() {
                return this.async(new Promise((resolve, reject) => {
                    try {
                        this.updateUserPreferencesWhenAvailable_recursive(0, resolve, reject);
                    }
                    catch (e) {
                        reject(e);
                    }
                }));
            },
            updateUserPreferencesWhenAvailable_recursive(attempt, resolve, reject) {
                if (attempt > 40) {
                    resolve();
                    return;
                }

                if (!this.userPreferences) {
                    setTimeout(() => {
                        attempt++;
                        this.updateUserPreferencesWhenAvailable_recursive(attempt, resolve, reject);
                    }, 50);
                }
                else {
                    // Only use the dashboards that exist for this user
                    this.dashboardIDs = _.intersection(this.userPreferences.tvDashboardIDs || [], _.map(this.dashboards, (d) => d.dashboardID));
                    this.rotationInterval = this.userPreferences.tvDashboardRotationInterval;
                    resolve();
                }
            }
		}
	};
</script>

<style lang="scss">
@import '~@/assets/styles/references';


.start-tv-modal {
    .checkbox-list { margin: 4px 0 gx(1) 0; }
    .checkbox-list-header { margin: 0; padding: gx(0.5) 0 gx(0.25) 0; color: color(primary); font-size: $h6-font-size; }
}
</style>
